export default defineAppConfig({
  ui: {
    // ── Palette ───────────────────────────────────────────────────────────────
    colors: {
      primary: 'green',
      neutral: 'zinc',
    },

    // ── Button ────────────────────────────────────────────────────────────────
    // Already rounded-md by default. Add transition-all for hover lift feel,
    // and set default size to sm to match our compact Premium aesthetic.
    button: {
      slots: {
        base: 'rounded-lg font-medium inline-flex items-center disabled:cursor-not-allowed aria-disabled:cursor-not-allowed disabled:opacity-75 aria-disabled:opacity-75 transition-all duration-150',
      },
      defaultVariants: {
        size: 'sm',
      },
    },

    // ── Input / Textarea ──────────────────────────────────────────────────────
    // The `outline` variant sets `ring ring-inset ring-accented` (accented =
    // gray-200/700). We add transition-colors and keep the default sm size.
    input: {
      slots: {
        base: 'w-full rounded-lg border-0 appearance-none placeholder:text-dimmed focus:outline-none disabled:cursor-not-allowed disabled:opacity-75 transition-colors duration-150',
      },
      defaultVariants: {
        size: 'sm',
        color: 'primary',
        variant: 'outline',
      },
    },

    select: {
      slots: {
        base: 'w-full rounded-lg border-0 appearance-none focus:outline-none disabled:cursor-not-allowed disabled:opacity-75 transition-colors duration-150 cursor-pointer',
      },
      defaultVariants: {
        size: 'sm',
        color: 'primary',
        variant: 'outline',
      },
    },

    textarea: {
      slots: {
        base: 'w-full rounded-lg border-0 appearance-none placeholder:text-dimmed focus:outline-none disabled:cursor-not-allowed disabled:opacity-75 transition-colors duration-150',
      },
      defaultVariants: {
        size: 'sm',
        color: 'primary',
        variant: 'outline',
      },
    },

    // ── Card ──────────────────────────────────────────────────────────────────
    // NuxtUI Card has a `variant` prop: outline | soft | subtle | solid.
    // The `outline` variant adds `ring ring-default divide-y divide-default bg-default`.
    // We round it more and remove extra padding to keep it clean.
    card: {
      slots: {
        root: 'rounded-2xl overflow-hidden',
        header: 'px-5 py-4',
        body: 'px-5 py-5',
        footer: 'px-5 py-4',
      },
      defaultVariants: {
        variant: 'outline',
      },
    },

    // ── Modal ─────────────────────────────────────────────────────────────────
    // The overlay already has bg-elevated/75 from the `overlay: true` variant.
    // We add backdrop-blur-sm to the overlay slot and round the content more.
    modal: {
      slots: {
        overlay: 'fixed inset-0 backdrop-blur-sm',
        content: 'bg-default divide-y divide-default flex flex-col focus:outline-none rounded-2xl',
        header: 'flex items-center gap-1.5 px-5 py-4 min-h-(--ui-header-height)',
        body: 'flex-1 px-5 py-5',
        footer: 'flex items-center gap-1.5 px-5 py-4',
        title: 'text-highlighted font-semibold',
        description: 'mt-1 text-muted text-sm',
      },
    },

    // ── Slideover ─────────────────────────────────────────────────────────────
    slideover: {
      slots: {
        overlay: 'fixed inset-0 backdrop-blur-sm',
        header: 'flex items-center gap-1.5 px-5 py-4 min-h-(--ui-header-height)',
        body: 'flex-1 overflow-y-auto px-5 py-5',
        footer: 'flex items-center gap-1.5 px-5 py-4',
        title: 'text-highlighted font-semibold',
        description: 'mt-1 text-muted text-sm',
      },
    },

    // ── Badge ─────────────────────────────────────────────────────────────────
    badge: {
      slots: {
        base: 'rounded-md font-medium inline-flex items-center',
      },
    },

    // ── FormField ─────────────────────────────────────────────────────────────
    formField: {
      slots: {
        label: 'block text-xs font-medium text-muted mb-1',
        hint: 'text-xs text-muted',
        error: 'mt-0.5 text-xs text-error',
      },
    },

    // ── Separator ─────────────────────────────────────────────────────────────
    // Use a softer separator line consistent with the ring-1 aesthetic.
    separator: {
      slots: {
        border: 'border-default/60',
      },
    },

    // ── NavigationMenu ────────────────────────────────────────────────────────
    // By default inactive links use text-muted (barely visible on coloured navbars).
    // Override to text-default so labels are always clearly readable.
    navigationMenu: {
      variants: {
        active: {
          false: {
            link: 'text-default',
            linkLeadingIcon: 'text-default',
          },
        },
      },
    },

    // ── Tooltip ───────────────────────────────────────────────────────────────
    tooltip: {
      slots: {
        content: 'rounded-lg px-2.5 py-1.5 text-xs font-medium shadow-lg',
      },
    },
  },
})
